import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../../context/auth-context';
import { ScenariosContext } from '../../context/scenarios-context';
import {
  fetchMyNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  clearAllNotifications,
  subscribeToNotifications,
} from '../../services/notificationService';
import { fetchProject } from '../../../account/tasks/services/myTasksService';
import Icon from '../UIElements/Icon';
import './styling/NotificationBell.css';

// Slow safety-net poll behind the SSE stream (subscribeToNotifications), and
// only while the tab is visible — the stream is the primary delivery path.
const FALLBACK_POLL_MS = 300_000;
const LIST_LIMIT = 30;

// Notification kind -> shared Icon name. Emoji here rasterised per platform
// and carried their own colours, so a row could not dim with its read state.
const TYPE_ICONS = {
  'task.assigned': 'taskAssigned',
  'task.status': 'taskStatus',
  'task.comment': 'comment',
  'task.mention': 'mention',
  'task.due': 'clock',
  'task.overdue': 'alert',
  'project.added': 'projects',
  'heap.rebuilt': 'heaps',
  'heap.rebuild_failed': 'alert',
};

function timeAgo(iso) {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

/**
 * Header bell: unread badge + dropdown of recent notifications. Clicking a
 * task notification selects that project and opens its management board
 * (same pattern as My Tasks' "Open task board" link); a project-access
 * notification opens the project's details page instead; a heap-rebuild
 * notification ('heap.rebuilt' | 'heap.rebuild_failed') selects the project
 * (and the scenario, when it carries one and it differs from the current
 * selection) and opens the Heaps page.
 */
const NotificationBell = () => {
  const auth = useContext(AuthContext);
  const scenariosCtx = useContext(ScenariosContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openingId, setOpeningId] = useState(null);
  const rootRef = useRef(null);
  const tokenRef = useRef(auth.token);
  tokenRef.current = auth.token;
  // Mirror of the notifications list so stream-event handling can inspect
  // current state without side effects inside React state updaters (which
  // run twice in StrictMode dev).
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  // Ids this tab just cleared — their echo "removed" events need no refetch.
  const dismissedRef = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const data = await fetchMyNotifications(tokenRef.current, { limit: LIST_LIMIT });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // Bell is best-effort — leave the last known state on error.
    }
  }, []);

  // Apply one pushed SSE event to local state. "created" and same-tab reads
  // resolve locally; a read event for a row we don't hold (other tab, older
  // than the visible page) falls back to one refetch so the badge stays true.
  const applyStreamEvent = useCallback(
    (kind, payload) => {
      if (kind === 'created' && payload.notification) {
        setNotifications((prev) =>
          [payload.notification, ...prev.filter((n) => n._id !== payload.notification._id)].slice(
            0,
            LIST_LIMIT
          )
        );
        setUnreadCount((c) => c + 1);
      } else if (kind === 'read') {
        const target = notificationsRef.current.find(
          (n) => n._id === payload.notificationId
        );
        if (!target) {
          refresh();
          return;
        }
        if (target.readAt) return; // this tab already applied it
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === payload.notificationId ? { ...n, readAt: payload.readAt } : n
          )
        );
      } else if (kind === 'read-all') {
        setNotifications((prev) =>
          prev.map((n) => (n.readAt ? n : { ...n, readAt: payload.readAt }))
        );
        setUnreadCount(0);
      } else if (kind === 'removed') {
        if (dismissedRef.current.delete(payload.notificationId)) return; // own echo
        const target = notificationsRef.current.find(
          (n) => n._id === payload.notificationId
        );
        if (!target) {
          refresh();
          return;
        }
        if (!target.readAt) setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) =>
          prev.filter((n) => n._id !== payload.notificationId)
        );
      } else if (kind === 'cleared') {
        setNotifications([]);
        setUnreadCount(0);
      }
    },
    [refresh]
  );

  // While logged in: one initial fetch, then live events over SSE, with a
  // slow visible-only poll as the safety net. The stream is only held open
  // while the tab is visible — the badge can't be seen from a hidden tab, so
  // a hidden tab costs the server nothing; regaining visibility reconnects
  // and refetches to catch anything missed.
  useEffect(() => {
    if (!auth.token) return undefined;
    let closeStream = null;
    const openStream = () => {
      if (!closeStream) {
        closeStream = subscribeToNotifications(tokenRef.current, applyStreamEvent);
      }
    };
    const shutStream = () => {
      if (closeStream) {
        closeStream();
        closeStream = null;
      }
    };
    refresh();
    if (document.visibilityState === 'visible') openStream();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, FALLBACK_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh();
        openStream();
      } else {
        shutStream();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      shutStream();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [auth.token, refresh, applyStreamEvent]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleItemClick = async (notification) => {
    if (openingId) return;
    setOpeningId(notification._id);
    try {
      if (!notification.readAt) {
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notification._id ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((c) => Math.max(0, c - 1));
        markNotificationRead(notification._id, tokenRef.current).catch(() => {});
      }
      if (notification.type === 'heap.rebuilt' || notification.type === 'heap.rebuild_failed') {
        if (notification.projectId) {
          const project = await fetchProject(notification.projectId, tokenRef.current);
          auth.setSelectedProject(project);
          const scenarioId = notification.scenarioId;
          const currentScenarioId = auth.selectedScenario?._id || auth.selectedScenario?.id;
          if (scenarioId && String(currentScenarioId) !== String(scenarioId)) {
            try {
              const projectRef = project._id || project.id;
              const scenarios = await scenariosCtx.fetchScenarios(projectRef, tokenRef.current);
              const match = scenarios.find((s) => String(s._id || s.id) === String(scenarioId));
              if (match) auth.setSelectedScenario(match);
            } catch {
              // Best effort — stay on whatever scenario was already selected.
            }
          }
        }
        navigate('/heaps');
        setOpen(false);
        return;
      }
      if (notification.projectId) {
        const project = await fetchProject(notification.projectId, tokenRef.current);
        auth.setSelectedProject(project);
        // A membership notification is about the project itself, not its task
        // board — and /management sits behind the section guard, which a brand
        // new member's project may have disabled.
        navigate(notification.type === 'project.added' ? '/project-details' : '/management');
      } else {
        navigate('/tasks');
      }
      setOpen(false);
    } catch {
      // Project may no longer be accessible — fall back to My Tasks.
      navigate('/tasks');
      setOpen(false);
    } finally {
      setOpeningId(null);
    }
  };

  const handleMarkAll = async () => {
    setNotifications((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }))
    );
    setUnreadCount(0);
    try {
      await markAllNotificationsRead(tokenRef.current);
    } catch {
      refresh();
    }
  };

  const handleDismiss = (event, notification) => {
    event.stopPropagation();
    dismissedRef.current.add(notification._id);
    setNotifications((prev) => prev.filter((n) => n._id !== notification._id));
    if (!notification.readAt) setUnreadCount((c) => Math.max(0, c - 1));
    deleteNotification(notification._id, tokenRef.current).catch(() => refresh());
  };

  const handleClearAll = async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await clearAllNotifications(tokenRef.current);
    } catch {
      refresh();
    }
  };

  if (!auth.isLoggedIn) return null;

  return (
    <div className="notif-bell" ref={rootRef}>
      <button
        type="button"
        className="notif-bell__button"
        title="Notifications"
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) refresh();
        }}
      >
        <Icon name="bell" size={19} />
        {unreadCount > 0 && (
          <span className="notif-bell__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-bell__panel">
          <div className="notif-bell__panel-head">
            <span className="notif-bell__panel-title">Notifications</span>
            <span className="notif-bell__panel-actions">
              {unreadCount > 0 && (
                <button type="button" className="notif-bell__mark-all" onClick={handleMarkAll}>
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button type="button" className="notif-bell__mark-all" onClick={handleClearAll}>
                  Clear all
                </button>
              )}
            </span>
          </div>
          <div className="notif-bell__list">
            {notifications.length === 0 && (
              <p className="notif-bell__empty">You're all caught up.</p>
            )}
            {notifications.map((n) => (
              <div key={n._id} className="notif-bell__row">
                <button
                  type="button"
                  className={`notif-bell__item${n.readAt ? '' : ' notif-bell__item--unread'}`}
                  disabled={openingId === n._id}
                  onClick={() => handleItemClick(n)}
                >
                  <span className="notif-bell__item-icon" aria-hidden="true">
                    <Icon name={TYPE_ICONS[n.type] || 'bell'} size={16} />
                  </span>
                  <span className="notif-bell__item-body">
                    <span className="notif-bell__item-title">{n.title}</span>
                    {n.body && <span className="notif-bell__item-sub">{n.body}</span>}
                    <span className="notif-bell__item-time">
                      {timeAgo(n.createdAt)}
                      {n.projectName ? ` · ${n.projectName}` : ''}
                    </span>
                  </span>
                  {!n.readAt && <span className="notif-bell__dot" aria-hidden="true" />}
                </button>
                <button
                  type="button"
                  className="notif-bell__dismiss"
                  title="Clear notification"
                  aria-label="Clear notification"
                  onClick={(e) => handleDismiss(e, n)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
